import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from './config';
import { useTheme } from './metasupply/components/ThemeContext';

interface SubscriptionPlan {
  id: number;
  name: string;
  display_name: string;
  description: string;
  monthly_price_cents: number;
  token_limit: number;
  is_active: boolean;
  features: Record<string, any>;
  sort_order: number;
  active_users: number;
  total_users: number;
}

interface UserUsage {
  tokens_used: number;
  queries_count: number;
  cost_usd: number;
  usage_percent: number;
}

interface UserSubscription {
  plan_id: number;
  plan_name: string;
  plan_display_name: string;
  token_limit: number;
  monthly_price_cents: number;
}

interface ManagedUser {
  id: number;
  email: string;
  full_name: string | null;
  is_active: boolean;
  is_admin: boolean;
  subscription: UserSubscription | null;
  usage: UserUsage;
  created_at: string | null;
  updated_at: string | null;
  created_by_email: string | null;
}

interface TokenUsageSummary {
  period_start: string;
  overview: {
    total_users: number;
    active_users: number;
    total_tokens_used: number;
    total_queries: number;
    total_cost: number;
    total_token_limit: number;
    overall_usage_percent: number;
  };
  by_plan: Array<{
    plan_name: string;
    token_limit: number;
    user_count: number;
    tokens_used: number;
    queries_count: number;
    cost_usd: number;
  }>;
  top_users: Array<{
    id: number;
    email: string;
    full_name: string | null;
    plan_name: string;
    tokens_used: number;
    queries_count: number;
    cost_usd: number;
    token_limit: number;
    usage_percent: number;
  }>;
}

const formatTokens = (tokens: number): string => {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(1)}K`;
  return tokens.toString();
};

const formatPrice = (cents: number): string => {
  if (cents === 0) return "Free";
  return `$${(cents / 100).toFixed(0)}`;
};

const SectionHeader = ({ children, sub }: { children: React.ReactNode; sub?: string }) => {
  const { T } = useTheme();
  return (
  <div style={{ marginBottom: 4 }}>
    <h2 style={{ fontSize: 24, fontWeight: 800, color: T.navy, letterSpacing: "-0.02em", lineHeight: 1.2 }}>{children}</h2>
    {sub && <p style={{ fontSize: 13, color: T.textLight, marginTop: 6, lineHeight: 1.5 }}>{sub}</p>}
  </div>
  );
};

const StatCard = ({ title, value, color, accent }: { title: string; value: string | number; color?: string; accent?: string }) => {
  const { T } = useTheme();
  return (
  <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 8, boxShadow: T.shadow, borderTop: accent ? `3px solid ${accent}` : "none" }}>
    <span style={{ fontSize: 11, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>{title}</span>
    <span style={{ fontSize: 26, fontWeight: 800, color: color || T.navy, letterSpacing: "-0.02em" }}>{value}</span>
  </div>
  );
};

const UsageBar = ({ percent, size = "normal" }: { percent: number; size?: "normal" | "small" }) => {
  const { T } = useTheme();
  const barHeight = size === "small" ? 6 : 8;
  const barColor = percent >= 90 ? T.danger : percent >= 75 ? T.warning : T.success;
  return (
    <div style={{ width: "100%", background: T.borderLight, borderRadius: barHeight / 2, height: barHeight, overflow: "hidden" }}>
      <div style={{ width: `${Math.min(percent, 100)}%`, height: "100%", background: barColor, borderRadius: barHeight / 2, transition: "width 0.5s ease" }} />
    </div>
  );
};

// ============================================================
// MODAL COMPONENTS
// ============================================================

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { email: string; password: string; full_name: string; is_admin: boolean; subscription_plan_id: number | null }) => Promise<void>;
  plans: SubscriptionPlan[];
}

function CreateUserModal({ isOpen, onClose, onSubmit, plans }: CreateUserModalProps) {
  const { T } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [planId, setPlanId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setPassword('');
      setFullName('');
      setIsAdmin(false);
      setPlanId(plans.find(p => p.name === 'free')?.id || null);
      setError(null);
    }
  }, [isOpen, plans]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ email, password, full_name: fullName, is_admin: isAdmin, subscription_plan_id: planId });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px', borderRadius: T.radiusSm,
    border: `1px solid ${T.border}`, background: T.white, color: T.textDark,
    fontSize: 14, outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 700, color: T.textLight,
    textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 6,
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: T.white, borderRadius: 16, width: '100%', maxWidth: 500, padding: 32, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h3 style={{ fontSize: 20, fontWeight: 800, color: T.navy }}>Create New User</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: T.textLight, cursor: 'pointer' }}>x</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle}>Email *</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required style={inputStyle} placeholder="user@company.com" />
            </div>
            <div>
              <label style={labelStyle}>Password *</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} style={inputStyle} placeholder="Min 8 characters" />
            </div>
            <div>
              <label style={labelStyle}>Full Name</label>
              <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} style={inputStyle} placeholder="John Doe" />
            </div>
            <div>
              <label style={labelStyle}>Subscription Plan</label>
              <select value={planId || ''} onChange={e => setPlanId(e.target.value ? Number(e.target.value) : null)} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">No plan</option>
                {plans.filter(p => p.is_active).map(p => (
                  <option key={p.id} value={p.id}>{p.display_name} - {formatPrice(p.monthly_price_cents)}/mo ({formatTokens(p.token_limit)} tokens)</option>
                ))}
              </select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.textMid, cursor: 'pointer' }}>
              <input type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} />
              Admin privileges
            </label>

            {error && (
              <div style={{ padding: '10px 14px', background: T.dangerBg, border: `1px solid #FECACA`, borderRadius: T.radiusSm, color: T.danger, fontSize: 13 }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" onClick={onClose} style={{ padding: '10px 20px', borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.white, color: T.textMid, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button type="submit" disabled={submitting} style={{ padding: '10px 24px', borderRadius: T.radiusSm, border: 'none', background: T.navy, color: T.white, cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, opacity: submitting ? 0.7 : 1 }}>
                {submitting ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (userId: number, data: { email?: string; full_name?: string; is_admin?: boolean; is_active?: boolean; subscription_plan_id?: number }) => Promise<void>;
  user: ManagedUser | null;
  plans: SubscriptionPlan[];
}

function EditUserModal({ isOpen, onClose, onSubmit, user, plans }: EditUserModalProps) {
  const { T } = useTheme();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [planId, setPlanId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && user) {
      setEmail(user.email);
      setFullName(user.full_name || '');
      setIsAdmin(user.is_admin);
      setIsActive(user.is_active);
      setPlanId(user.subscription?.plan_id || null);
      setError(null);
    }
  }, [isOpen, user]);

  if (!isOpen || !user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(user.id, {
        email,
        full_name: fullName,
        is_admin: isAdmin,
        is_active: isActive,
        subscription_plan_id: planId || undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px', borderRadius: T.radiusSm,
    border: `1px solid ${T.border}`, background: T.white, color: T.textDark,
    fontSize: 14, outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 700, color: T.textLight,
    textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 6,
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: T.white, borderRadius: 16, width: '100%', maxWidth: 500, padding: 32, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h3 style={{ fontSize: 20, fontWeight: 800, color: T.navy }}>Edit User</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: T.textLight, cursor: 'pointer' }}>x</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Full Name</label>
              <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Subscription Plan</label>
              <select value={planId || ''} onChange={e => setPlanId(e.target.value ? Number(e.target.value) : null)} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">No plan</option>
                {plans.filter(p => p.is_active).map(p => (
                  <option key={p.id} value={p.id}>{p.display_name} - {formatPrice(p.monthly_price_cents)}/mo ({formatTokens(p.token_limit)} tokens)</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 24 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.textMid, cursor: 'pointer' }}>
                <input type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} />
                Admin privileges
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.textMid, cursor: 'pointer' }}>
                <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                Active
              </label>
            </div>

            {user.usage && (
              <div style={{ padding: 14, background: T.offWhite, borderRadius: T.radiusSm, border: `1px solid ${T.borderLight}` }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Current Usage</div>
                <div style={{ display: 'flex', gap: 16, fontSize: 13, color: T.textMid }}>
                  <span>{formatTokens(user.usage.tokens_used)} tokens</span>
                  <span>{user.usage.queries_count} queries</span>
                  <span>${user.usage.cost_usd.toFixed(4)}</span>
                </div>
                <div style={{ marginTop: 8 }}>
                  <UsageBar percent={user.usage.usage_percent} size="small" />
                  <div style={{ fontSize: 11, color: T.textLight, marginTop: 4 }}>{user.usage.usage_percent}% of limit used</div>
                </div>
              </div>
            )}

            {error && (
              <div style={{ padding: '10px 14px', background: T.dangerBg, border: `1px solid #FECACA`, borderRadius: T.radiusSm, color: T.danger, fontSize: 13 }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" onClick={onClose} style={{ padding: '10px 20px', borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.white, color: T.textMid, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button type="submit" disabled={submitting} style={{ padding: '10px 24px', borderRadius: T.radiusSm, border: 'none', background: T.navy, color: T.white, cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, opacity: submitting ? 0.7 : 1 }}>
                {submitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// SUBSCRIPTION PLAN CARD
// ============================================================

function PlanCard({ plan, onEdit }: { plan: SubscriptionPlan; onEdit: (plan: SubscriptionPlan) => void }) {
  const { T } = useTheme();
  const features = plan.features || {};
  return (
    <div style={{
      background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
      padding: 24, boxShadow: T.shadow, display: 'flex', flexDirection: 'column', gap: 16,
      borderTop: `3px solid ${plan.name === 'enterprise' ? T.gold : plan.name === 'professional' ? T.navy : plan.name === 'starter' ? T.success : T.border}`,
      opacity: plan.is_active ? 1 : 0.6,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 800, color: T.navy, margin: 0 }}>{plan.display_name}</h3>
          <p style={{ fontSize: 12, color: T.textLight, marginTop: 4 }}>{plan.description}</p>
        </div>
        <button onClick={() => onEdit(plan)} style={{ padding: '6px 12px', borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.white, color: T.textMid, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Edit</button>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontSize: 32, fontWeight: 800, color: T.navy }}>{formatPrice(plan.monthly_price_cents)}</span>
        {plan.monthly_price_cents > 0 && <span style={{ fontSize: 13, color: T.textLight }}>/month</span>}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: T.textMid }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Token Limit</span>
          <span style={{ fontWeight: 700 }}>{formatTokens(plan.token_limit)}/mo</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Est. Queries</span>
          <span style={{ fontWeight: 700 }}>{features.max_queries_display || 'N/A'}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Support</span>
          <span style={{ fontWeight: 700 }}>{features.support || 'N/A'}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Data Exports</span>
          <span style={{ fontWeight: 700, color: features.data_exports ? T.success : T.textLight }}>{features.data_exports ? 'Yes' : 'No'}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>API Access</span>
          <span style={{ fontWeight: 700, color: features.api_access ? T.success : T.textLight }}>{features.api_access ? 'Yes' : 'No'}</span>
        </div>
      </div>

      <div style={{ borderTop: `1px solid ${T.borderLight}`, paddingTop: 12, display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.textLight }}>
        <span>{plan.active_users} active user{plan.active_users !== 1 ? 's' : ''}</span>
        {!plan.is_active && <span style={{ color: T.danger, fontWeight: 600 }}>Inactive</span>}
      </div>
    </div>
  );
}

// ============================================================
// EDIT PLAN MODAL
// ============================================================

interface EditPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (planId: number, data: any) => Promise<void>;
  plan: SubscriptionPlan | null;
}

function EditPlanModal({ isOpen, onClose, onSubmit, plan }: EditPlanModalProps) {
  const { T } = useTheme();
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [priceCents, setPriceCents] = useState(0);
  const [tokenLimit, setTokenLimit] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && plan) {
      setDisplayName(plan.display_name);
      setDescription(plan.description || '');
      setPriceCents(plan.monthly_price_cents);
      setTokenLimit(plan.token_limit);
      setIsActive(plan.is_active);
      setError(null);
    }
  }, [isOpen, plan]);

  if (!isOpen || !plan) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(plan.id, {
        display_name: displayName,
        description,
        monthly_price_cents: priceCents,
        token_limit: tokenLimit,
        is_active: isActive,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update plan');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px', borderRadius: T.radiusSm,
    border: `1px solid ${T.border}`, background: T.white, color: T.textDark,
    fontSize: 14, outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 700, color: T.textLight,
    textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 6,
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: T.white, borderRadius: 16, width: '100%', maxWidth: 500, padding: 32, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h3 style={{ fontSize: 20, fontWeight: 800, color: T.navy }}>Edit Plan: {plan.display_name}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: T.textLight, cursor: 'pointer' }}>x</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle}>Display Name</label>
              <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Description</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} style={{ ...inputStyle, minHeight: 60, resize: 'vertical' as const }} />
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Monthly Price (cents)</label>
                <input type="number" value={priceCents} onChange={e => setPriceCents(Number(e.target.value))} min={0} style={inputStyle} />
                <div style={{ fontSize: 11, color: T.textLight, marginTop: 4 }}>= ${(priceCents / 100).toFixed(2)}/mo</div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Token Limit (monthly)</label>
                <input type="number" value={tokenLimit} onChange={e => setTokenLimit(Number(e.target.value))} min={0} style={inputStyle} />
                <div style={{ fontSize: 11, color: T.textLight, marginTop: 4 }}>= {formatTokens(tokenLimit)} tokens</div>
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.textMid, cursor: 'pointer' }}>
              <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
              Active (visible to new users)
            </label>

            {error && (
              <div style={{ padding: '10px 14px', background: T.dangerBg, border: `1px solid #FECACA`, borderRadius: T.radiusSm, color: T.danger, fontSize: 13 }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" onClick={onClose} style={{ padding: '10px 20px', borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.white, color: T.textMid, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button type="submit" disabled={submitting} style={{ padding: '10px 24px', borderRadius: T.radiusSm, border: 'none', background: T.navy, color: T.white, cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, opacity: submitting ? 0.7 : 1 }}>
                {submitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

interface AdminUserManagementProps {
  activeTab: 'users' | 'subscriptions' | 'usage';
}

export default function AdminUserManagement({ activeTab }: AdminUserManagementProps) {
  const { T } = useTheme();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [usageSummary, setUsageSummary] = useState<TokenUsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [editingUser, setEditingUser] = useState<ManagedUser | null>(null);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/users`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users);
      }
    } catch (e) {
      console.error('Failed to fetch users:', e);
    }
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/subscriptions`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPlans(data.plans);
      }
    } catch (e) {
      console.error('Failed to fetch plans:', e);
    }
  }, []);

  const fetchUsageSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/token-usage/summary`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUsageSummary(data);
      }
    } catch (e) {
      console.error('Failed to fetch usage summary:', e);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchPlans(), fetchUsageSummary()]);
      setLoading(false);
    };
    load();
  }, [fetchUsers, fetchPlans, fetchUsageSummary]);

  const handleCreateUser = async (data: { email: string; password: string; full_name: string; is_admin: boolean; subscription_plan_id: number | null }) => {
    const res = await fetch(`${API_BASE}/admin/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to create user' }));
      throw new Error(err.detail || 'Failed to create user');
    }
    await fetchUsers();
  };

  const handleUpdateUser = async (userId: number, data: any) => {
    const res = await fetch(`${API_BASE}/admin/users/${userId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to update user' }));
      throw new Error(err.detail || 'Failed to update user');
    }
    await fetchUsers();
  };

  const handleUpdatePlan = async (planId: number, data: any) => {
    const res = await fetch(`${API_BASE}/admin/subscriptions/${planId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to update plan' }));
      throw new Error(err.detail || 'Failed to update plan');
    }
    await fetchPlans();
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, color: T.textLight }}>
        Loading...
      </div>
    );
  }

  // ============================================================
  // USERS TAB
  // ============================================================
  if (activeTab === 'users') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <SectionHeader sub="Create, manage, and assign subscriptions to users">User Management</SectionHeader>
          <button onClick={() => setShowCreateUser(true)} style={{ padding: '10px 20px', borderRadius: T.radiusSm, border: 'none', background: T.navy, color: T.white, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            + New User
          </button>
        </div>

        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: 'hidden', boxShadow: T.shadow }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: T.offWhite, borderBottom: `1px solid ${T.border}` }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: T.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>User</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: T.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Plan</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: T.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Token Usage</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: T.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Queries</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: T.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Status</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: T.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ fontWeight: 600, color: T.textDark }}>{user.full_name || user.email}</div>
                    <div style={{ fontSize: 12, color: T.textLight }}>{user.email}</div>
                    {user.is_admin && <span style={{ fontSize: 10, fontWeight: 700, color: T.gold, background: `${T.gold}15`, padding: '2px 6px', borderRadius: 4, marginTop: 2, display: 'inline-block' }}>ADMIN</span>}
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    {user.subscription ? (
                      <div>
                        <div style={{ fontWeight: 600, color: T.textDark }}>{user.subscription.plan_display_name}</div>
                        <div style={{ fontSize: 12, color: T.textLight }}>
                          {formatPrice(user.subscription.monthly_price_cents)}/mo
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: T.textLight }}>No plan</span>
                    )}
                  </td>
                  <td style={{ padding: '14px 16px', minWidth: 160 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>{formatTokens(user.usage.tokens_used)}</span>
                      {user.subscription && <span style={{ fontSize: 11, color: T.textLight }}>/ {formatTokens(user.subscription.token_limit)}</span>}
                    </div>
                    <UsageBar percent={user.usage.usage_percent} size="small" />
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ fontWeight: 600 }}>{user.usage.queries_count}</span>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{
                      padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                      background: user.is_active ? T.successBg : T.dangerBg,
                      color: user.is_active ? T.success : T.danger,
                    }}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                    <button onClick={() => setEditingUser(user)} style={{ padding: '6px 14px', borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.white, color: T.textMid, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: 40, textAlign: 'center', color: T.textLight }}>No users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <CreateUserModal isOpen={showCreateUser} onClose={() => setShowCreateUser(false)} onSubmit={handleCreateUser} plans={plans} />
        <EditUserModal isOpen={!!editingUser} onClose={() => setEditingUser(null)} onSubmit={handleUpdateUser} user={editingUser} plans={plans} />
      </div>
    );
  }

  // ============================================================
  // SUBSCRIPTIONS TAB
  // ============================================================
  if (activeTab === 'subscriptions') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <SectionHeader sub="Configure subscription tiers, pricing, and token limits">Subscription Plans</SectionHeader>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
          {plans.map(plan => (
            <PlanCard key={plan.id} plan={plan} onEdit={(p) => setEditingPlan(p)} />
          ))}
        </div>

        <EditPlanModal isOpen={!!editingPlan} onClose={() => setEditingPlan(null)} onSubmit={handleUpdatePlan} plan={editingPlan} />
      </div>
    );
  }

  // ============================================================
  // TOKEN USAGE TAB
  // ============================================================
  if (activeTab === 'usage') {
    const overview = usageSummary?.overview;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <SectionHeader sub="Track token consumption, costs, and usage patterns across all users">Token Usage</SectionHeader>
          <button onClick={fetchUsageSummary} style={{ padding: '8px 16px', borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.white, color: T.textMid, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            Refresh
          </button>
        </div>

        {usageSummary && (
          <div style={{ fontSize: 12, color: T.textLight }}>
            Billing period: {usageSummary.period_start}
          </div>
        )}

        {overview && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
            <StatCard title="Active Users" value={overview.active_users} accent={T.navy} />
            <StatCard title="Total Tokens Used" value={formatTokens(overview.total_tokens_used)} accent={T.gold} />
            <StatCard title="Total Queries" value={overview.total_queries} accent={T.success} />
            <StatCard title="Total Cost" value={`$${overview.total_cost.toFixed(2)}`} accent={T.warning} />
            <StatCard title="Overall Usage" value={`${overview.overall_usage_percent}%`} accent={overview.overall_usage_percent >= 80 ? T.danger : T.success} />
          </div>
        )}

        {usageSummary?.by_plan && usageSummary.by_plan.length > 0 && (
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: T.navy, marginBottom: 12 }}>Usage by Plan</h3>
            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: 'hidden', boxShadow: T.shadow }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: T.offWhite, borderBottom: `1px solid ${T.border}` }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: T.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Plan</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: T.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Users</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: T.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Tokens Used</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: T.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Queries</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: T.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {usageSummary.by_plan.map((p, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                      <td style={{ padding: '12px 16px', fontWeight: 600 }}>{p.plan_name}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>{p.user_count}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>{formatTokens(p.tokens_used)}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>{p.queries_count}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>${p.cost_usd.toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {usageSummary?.top_users && usageSummary.top_users.length > 0 && (
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: T.navy, marginBottom: 12 }}>Top Users This Period</h3>
            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: 'hidden', boxShadow: T.shadow }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: T.offWhite, borderBottom: `1px solid ${T.border}` }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: T.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>User</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: T.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Plan</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: T.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Tokens</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: T.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Queries</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: T.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Usage</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: T.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {usageSummary.top_users.map(u => (
                    <tr key={u.id} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 600 }}>{u.full_name || u.email}</div>
                        {u.full_name && <div style={{ fontSize: 11, color: T.textLight }}>{u.email}</div>}
                      </td>
                      <td style={{ padding: '12px 16px' }}>{u.plan_name || 'None'}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>{formatTokens(u.tokens_used)}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>{u.queries_count}</td>
                      <td style={{ padding: '12px 16px', minWidth: 120 }}>
                        <UsageBar percent={u.usage_percent} size="small" />
                        <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>{u.usage_percent}%</div>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>${u.cost_usd.toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}
